import React from 'react';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Mail from 'lucide-react/dist/esm/icons/mail.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { usePageSections } from '../../../hooks/usePageSections';
import { resolveCtaManualEnvelopeMode } from '../../../utils/ctaHelpers';
import {
  HowToPlayStep1Title,
  HowToPlayStep1Desc,
  HowToPlayStep2Title,
  HowToPlayStep2Desc,
  HowToPlayStep3Title,
  HowToPlayStep3Desc,
  HowToPlayStep4Title,
  HowToPlayStep4Desc,
} from '../../../content/keys';

export default function HowToStepsChaseTheAceSection({ section }) {
  const { c } = useContent();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { t: tr } = useTranslations();
  const { siteDetails } = useSiteConfig();
  const { sections: homeSections } = usePageSections('chasetheace_home');
  const isManualEnvelopeMode = resolveCtaManualEnvelopeMode(siteDetails, homeSections);

  const t = (key, legacyKey, fallback) => {
    const v = c(key) || (legacyKey ? c(legacyKey) : null);
    if (v) return v;
    const trVal = tr(key);
    if (trVal && trVal !== key) return trVal;
    return fallback || '';
  };

  const steps = isManualEnvelopeMode ? [
    {
      icon: Ticket,
      titleKey: 'chaseTheAce.step1Title',
      descKey: 'chaseTheAce.step1Desc',
      legacyTitleKey: HowToPlayStep1Title,
      legacyDescKey: HowToPlayStep1Desc,
      fallbackTitle: 'Buy Your Tickets',
      fallbackDesc: 'Purchase tickets for the weekly draw. Every ticket gives you a chance to win!',
    },
    {
      icon: Mail,
      titleKey: 'chaseTheAce.step2Title',
      descKey: 'chaseTheAce.step2Desc',
      legacyTitleKey: HowToPlayStep2Title,
      legacyDescKey: HowToPlayStep2Desc,
      fallbackTitle: 'Weekly Draw',
      fallbackDesc: 'Each week, a winning ticket is drawn and an envelope is selected.',
    },
    {
      icon: Mail,
      titleKey: 'chaseTheAce.step3Title',
      descKey: 'chaseTheAce.step3Desc',
      legacyTitleKey: HowToPlayStep3Title,
      legacyDescKey: HowToPlayStep3Desc,
      fallbackTitle: 'Pick an Envelope',
      fallbackDesc: 'The winner selects an envelope containing a playing card from the deck.',
    },
    {
      icon: Sparkles,
      titleKey: 'chaseTheAce.step4Title',
      descKey: 'chaseTheAce.step4Desc',
      legacyTitleKey: HowToPlayStep4Title,
      legacyDescKey: HowToPlayStep4Desc,
      fallbackTitle: 'Find the Ace',
      fallbackDesc: 'If the jackpot card is drawn, win the JACKPOT! If not, the pot rolls over.',
    }
  ] : [
    {
      icon: Ticket,
      titleKey: 'chaseTheAce.step1Title',
      descKey: 'chaseTheAce.step1Desc',
      legacyTitleKey: HowToPlayStep1Title,
      legacyDescKey: HowToPlayStep1Desc,
      fallbackTitle: 'Buy Tickets & Choose Envelope',
      fallbackDesc: 'Purchase tickets and select your lucky envelope for the weekly draw.',
    },
    {
      icon: Mail,
      titleKey: 'chaseTheAce.step2Title',
      descKey: 'chaseTheAce.step2Desc',
      legacyTitleKey: HowToPlayStep2Title,
      legacyDescKey: HowToPlayStep2Desc,
      fallbackTitle: 'Weekly Draw',
      fallbackDesc: 'Each week, a winning ticket number is drawn.',
    },
    {
      icon: Mail,
      titleKey: 'chaseTheAce.step3Title',
      descKey: 'chaseTheAce.step3Desc',
      legacyTitleKey: HowToPlayStep3Title,
      legacyDescKey: HowToPlayStep3Desc,
      fallbackTitle: 'Reveal the Envelope',
      fallbackDesc: "The winner's chosen envelope is opened live to reveal the card inside.",
    },
    {
      icon: Sparkles,
      titleKey: 'chaseTheAce.step4Title',
      descKey: 'chaseTheAce.step4Desc',
      legacyTitleKey: HowToPlayStep4Title,
      legacyDescKey: HowToPlayStep4Desc,
      fallbackTitle: 'Find the Ace',
      fallbackDesc: 'If the jackpot card is in the envelope, win the JACKPOT! If not, the pot rolls over.',
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-6 md:gap-8 mb-8 sm:mb-16 md:mb-20">
      {steps.map((step, index) => {
        const Icon = step.icon;
        return (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-md sm:shadow-lg border border-gray-200 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
          >
            <div>
              {/* Header: Step Pill on Left, Icon on Right */}
              <div className="flex items-center justify-between gap-2 mb-3.5">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider bg-charity-primary/10 text-charity-primary border border-charity-primary/20">
                  Step {index + 1}
                </span>
                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-md shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--charity-primary, #1e40af), var(--charity-accent, #06b6d4))' }}
                >
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>

              <InlineTranslationEdit
                translationKey={step.titleKey}
                value={t(step.titleKey, step.legacyTitleKey, step.fallbackTitle)}
                fallback={step.fallbackTitle}
                editMode={editMode}
                onUpdate={handleTranslationUpdate}
                className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1.5 block text-left"
              />
              <InlineTranslationEdit
                translationKey={step.descKey}
                value={t(step.descKey, step.legacyDescKey, step.fallbackDesc)}
                fallback={step.fallbackDesc}
                editMode={editMode}
                onUpdate={handleTranslationUpdate}
                className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm leading-relaxed block text-left"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
